import { SyntheticEvent, useCallback } from 'react';
import {
  DeleteVoicemailResponse,
  SetVoicemailViewStatusResponse,
  ViewStatus,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { UseMutateFunction, useQueryClient } from 'react-query';
import { VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import { ActionsUI } from '@frontend/contact-actions';
import { PhoneNumberPopover } from '@frontend/contact-actions-place-call';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { i18next, useTranslation } from '@frontend/i18n';
import { sanitizePhoneNumber } from '@frontend/phone-numbers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { getActionIcon } from '../../ui-utils';
import { getFormattedCallerName } from '../../utils';
import { Action } from '../all-calls/shared-buttons';
import { VoicemailTableDataRow } from '../all-calls/types';
import { TranscriptionContext } from '../voicemail-details/types';
import { downloadVoicemail } from './voicemail-utils';
import { ForwardVoicemailDynamicData } from './voicemails-multi.component';

export const getFallbackCallerName = (contactNumber: string): string => {
  const sanitizedNumber = sanitizePhoneNumber(contactNumber ?? '');
  return !!sanitizedNumber ? getFormattedCallerName(sanitizedNumber) : i18next.t('Unknown', { ns: 'calls' });
};

export const getShortHandName = (callInfo: VoicemailTableDataRow): string => {
  const { contactName, contactNumber } = callInfo;
  return contactName !== '' ? contactName : getFallbackCallerName(contactNumber);
};

export type VoicemailActionsGetterProps = {
  index: number;
  rowData: VoicemailTableDataRow;
  triggerCall: (e?: SyntheticEvent, num?: string) => void;
  addTag: () => void;
  multipleContactNumbers: ReturnType<typeof ActionsUI.actions.usePhoneCallAction>['multipleContactNumbers'];
};

/**
 * Handle as much as we can for actions on voicemail rows.
 * Eventually we will need input from the parent to perform some actions, so we pass those in as props.
 */
export const useVoicemailActionsGetter = ({
  selectedLocationIds,
  searchQuery,
  openForwardVoicemailModal,
  updateReadStatus,
  deleteVoicemail,
  trackingIdPrefix,
}: {
  selectedLocationIds: string[];
  searchQuery: string;
  openForwardVoicemailModal: (props: ForwardVoicemailDynamicData) => void;
  updateReadStatus: UseMutateFunction<SetVoicemailViewStatusResponse, unknown, VmTypes.StatusPayload, unknown>;
  deleteVoicemail: UseMutateFunction<DeleteVoicemailResponse, unknown, VmTypes.DeletePayload, void>;
  trackingIdPrefix?: string;
}) => {
  const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();

  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore<TranscriptionContext>('setShow');

  const queryClient = useQueryClient();
  const { t } = useTranslation('calls');
  const { setShow: openPanel } = useCustomContactFormSlidePanel();
  const alert = useAlert();

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const clickToCallFeatureFlag = getFeatureFlagValue('click-to-call-multiple-numbers');

  return useCallback(
    ({ index, rowData, triggerCall, addTag, multipleContactNumbers }: VoicemailActionsGetterProps) =>
      getVoicemailActions({
        trackingIdPrefix,
        rowData,
        triggerCall,
        clickToCallFeatureFlag,
        multipleContactNumbers,
        triggerMessage: () => {
          messageOnClick({
            personId: rowData.contactId,
            personPhone: rowData.contactNumber,
            groupIds: rowData.locationIds,
          });
        },
        viewProfile: () => {
          if (!!rowData.contactId) {
            setPersonId(rowData.contactId, true, selectedLocationIds[0]);
            setShow(true, 'contact');
          }
        },
        createContact: () => {
          const number = rowData.contactNumber;

          const digitsOnly = number ? sanitizePhoneNumber(number) : '';
          const hasCountryCode = digitsOnly.startsWith('1');

          openPanel({
            show: true,
            context: {
              mode: 'create',
              person: {
                MobilePhone: hasCountryCode ? digitsOnly.slice(1) : digitsOnly,
              },
              locationId: rowData.locationIds[0],
              onSave: () => {
                alert.success(t('Contact created successfully.'));

                // This line doesn't quite accomplish anything because the logs do not update with the newly created contact
                queryClient.invalidateQueries({ queryKey: queryKeys.multiVoicemails(searchQuery) });
                queryClient.invalidateQueries({
                  predicate: (query) => query.queryKey.includes('contacts'),
                });
              },
              onError: () => {
                alert.error(t('There was an error creating your contact. Please try again.'));
              },
            },
          });
        },
        forwardVoicemail: () => {
          openForwardVoicemailModal({
            rowIndex: index,
            vmId: rowData.voicemailId == '' ? rowData.channelId : rowData.voicemailId,
            mediaId: rowData.mediaId,
            mediaPath: rowData.mediaFilePath,
            currentVoicemailBoxId: rowData.mailboxId,
            currentVoicemailBoxName: rowData.voiceMailBoxName,
          } as ForwardVoicemailDynamicData);
        },
        addTag,
        downloadVoicemail: () => {
          downloadVoicemail(rowData.mediaFilePath, rowData.contactName ?? 'unknown', rowData.dateTime);
        },
        markAsRead: () => {
          if (!!rowData) {
            updateReadStatus({
              index,
              voicemailId: rowData?.forwardedMessageId || rowData.channelId,
              status: ViewStatus.VIEW_STATUS_READ,
            });
          }
        },
        markAsUnread: () => {
          if (!!rowData) {
            updateReadStatus({
              index,
              voicemailId: rowData?.forwardedMessageId || rowData.channelId,
              status: ViewStatus.VIEW_STATUS_UNREAD,
            });
          }
        },
        deleteVoicemail: () => {
          if (!!rowData) {
            deleteVoicemail({
              voicemailId: rowData?.forwardedMessageId || rowData.channelId,
              index,
              mailboxId: rowData.mailboxId,
            });
          }
        },
      }),
    [
      trackingIdPrefix,
      messageOnClick,
      setPersonId,
      selectedLocationIds,
      setShow,
      openPanel,
      alert,
      t,
      queryClient,
      searchQuery,
      openForwardVoicemailModal,
      updateReadStatus,
      deleteVoicemail,
      clickToCallFeatureFlag,
    ]
  );
};

export const getVoicemailActions = ({
  rowData,
  multipleContactNumbers,
  downloadVoicemail,
  clickToCallFeatureFlag,
  deleteVoicemail,
  triggerCall,
  triggerMessage,
  viewProfile,
  createContact,
  forwardVoicemail,
  addTag,
  markAsRead,
  markAsUnread,
  trackingIdPrefix = 'phone-portal-weave2.0-voicemails-btn',
}: {
  rowData: VoicemailTableDataRow;
  multipleContactNumbers: ReturnType<typeof ActionsUI.actions.usePhoneCallAction>['multipleContactNumbers'];
  triggerCall: (e?: SyntheticEvent, num?: string) => void;
  clickToCallFeatureFlag: boolean;
  triggerMessage: () => void;
  viewProfile: () => void;
  createContact: () => void;
  forwardVoicemail: () => void;
  addTag: () => void;
  markAsRead: () => void;
  markAsUnread: () => void;
  deleteVoicemail: () => void;
  downloadVoicemail: () => void;
  trackingIdPrefix?: string;
}) => {
  const showPhoneNumberPopover = multipleContactNumbers.length > 1 && clickToCallFeatureFlag;
  const actions: Action[] = [
    {
      Icon: () => getActionIcon({ iconName: 'phone' }),
      trackingId: `${trackingIdPrefix}-tablerowaction-call`,
      label: `${i18next.t('Call', { ns: 'calls' })} ${getShortHandName(rowData)}`,
      onClick: showPhoneNumberPopover ? undefined : triggerCall,
      subMenuItems: showPhoneNumberPopover
        ? ({ menu: { getItemProps } }) => {
            return (
              <PhoneNumberPopover
                getItemProps={getItemProps}
                data={multipleContactNumbers}
                onClick={triggerCall}
                trackingIdSuffix='phone-portal-weave2.0-calls-btn-tablerowaction-call-item'
              />
            );
          }
        : undefined,
      id: 'call-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'message' }),
      trackingId: `${trackingIdPrefix}-tablerowaction-message`,
      label: `${i18next.t('Message', { ns: 'calls' })} ${getShortHandName(rowData)}`,
      onClick: triggerMessage,
      id: 'message-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'user' }),
      label: i18next.t('View Profile', { ns: 'calls' }),
      trackingId: `${trackingIdPrefix}-tablerowaction-viewprofile`,
      hide: !rowData.contactId,
      onClick: viewProfile,
      id: 'view-profile-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'user-management' }),
      hide: !!rowData.contactId,
      trackingId: `${trackingIdPrefix}-tablerowaction-createcontact`,
      label: i18next.t('Create Contact', { ns: 'calls' }),
      onClick: createContact,
      id: 'create-contact-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'voicemail-override' }),
      label: i18next.t('Forward Voicemail', { ns: 'calls' }),
      trackingId: `${trackingIdPrefix}-tablerowaction-forwardvoicemail`,
      onClick: forwardVoicemail,
      id: 'forward-voicemail-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'label' }),
      label: i18next.t('Add Tag', { ns: 'calls' }),
      trackingId: `${trackingIdPrefix}-tablerowaction-tagvoicemail`,
      onClick: addTag,
      id: 'add-tag-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'download' }),
      label: i18next.t('Download Voicemail', { ns: 'calls' }),
      trackingId: `${trackingIdPrefix}-tablerowaction-downloadvoicemail`,
      onClick: downloadVoicemail,
      id: 'download-voicemail-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'mark-unread' }),
      label: i18next.t('Mark Unread', { ns: 'calls' }),
      trackingId: `${trackingIdPrefix}-tablerowaction-togglestatus`,
      hide: !rowData.readAt,
      onClick: markAsUnread,
      id: 'mark-unread-action',
    },
    {
      Icon: () => getActionIcon({ iconName: 'mark-read' }),
      label: i18next.t('Mark Read', { ns: 'calls' }),
      trackingId: 'phone-portal-weave2.0-voicemails-btn-tablerowaction-togglestatus',
      hide: !!rowData.readAt,
      onClick: markAsRead,
      id: 'mark-read-action',
    },
    {
      Icon: (props) => getActionIcon({ iconName: 'trash', color: props.color }),
      label: i18next.t('Delete Voicemail', { ns: 'calls' }),
      trackingId: 'phone-portal-weave2.0-voicemails-btn-tablerowaction-deletevoicemail',
      destructive: true,
      onClick: deleteVoicemail,
      id: 'delete-voicemail-action',
    },
  ];

  return actions;
};
